import type { FrappeApp } from 'frappe-js-sdk'

import type { ResponseWrapper } from '@/types/external/2work/api/response/ResponseWrapper'

// Frappe validates the CSRF token of cookie sessions for state changing requests.
// The token only exists after it was created for the session, e.g. by rendering the desk
// (iframes) or by the dedicated endpoint below. This module fetches the token of the
// current session once, adds it to every state changing request and renews it after
// session changes (login, logout) or a rejected token.

type FrappeAxiosInstance = FrappeApp['axios']
type FrappeAxiosRequestInterceptor = NonNullable<
  Parameters<FrappeAxiosInstance['interceptors']['request']['use']>[0]
>
type FrappeAxiosRequestConfig = Parameters<FrappeAxiosRequestInterceptor>[0]

interface CsrfTokenRequestConfig extends FrappeAxiosRequestConfig {
  isCsrfTokenRetry?: boolean
}

interface CsrfTokenRejectedError {
  config: CsrfTokenRequestConfig
}

interface CsrfTokenData {
  csrf_token: string
}

const csrfTokenHeaderName = 'X-Frappe-CSRF-Token'
const csrfTokenEndpoint = '/api/method/erp_2work.erp_2work.controller.csrf.get_csrf_token'
const csrfTokenErrorType = 'CSRFTokenError'
const csrfTokenErrorHttpStatus = 400
// requests which create a new session: the guest session has no token yet
const sessionCreatingEndpoints = ['/api/method/login']
// requests after which the session (and therefore the token) changed
const sessionChangingEndpoints = ['/api/method/login', '/api/method/logout']
const unsafeHttpMethods = new Set(['delete', 'patch', 'post', 'put'])

const isUnsafeRequest = (requestConfig: FrappeAxiosRequestConfig): boolean =>
  unsafeHttpMethods.has((requestConfig.method ?? 'get').toLowerCase())

const isEndpointRequest = (
  requestConfig: FrappeAxiosRequestConfig | undefined,
  endpoints: string[],
): boolean => endpoints.some((endpoint) => requestConfig?.url?.startsWith(endpoint) === true)

const isCsrfTokenRejectedError = (
  possibleRejectedError: unknown,
): possibleRejectedError is CsrfTokenRejectedError => {
  if (
    !(possibleRejectedError instanceof Object) ||
    !('config' in possibleRejectedError) ||
    !('response' in possibleRejectedError)
  ) {
    return false
  }
  const { response } = possibleRejectedError

  return (
    response instanceof Object &&
    'status' in response &&
    response.status === csrfTokenErrorHttpStatus &&
    'data' in response &&
    response.data instanceof Object &&
    'exc_type' in response.data &&
    response.data.exc_type === csrfTokenErrorType
  )
}

const installFrappeCsrfTokenHandling = (frappeAxiosInstance: FrappeAxiosInstance): void => {
  let csrfToken: null | string = null
  let pendingCsrfTokenRequest: null | Promise<null | string> = null

  // without a token (guest session, missing endpoint) requests are sent unchanged
  const requestCsrfToken = async (): Promise<null | string> =>
    await frappeAxiosInstance
      .get<ResponseWrapper<CsrfTokenData>>(csrfTokenEndpoint)
      .then((tokenResponse) => {
        csrfToken = tokenResponse.data.data.csrf_token
        return csrfToken
      })
      .catch(() => null)
      .finally(() => {
        pendingCsrfTokenRequest = null
      })

  // fetch the token once for all parallel requests
  const getCsrfToken = async (): Promise<null | string> => {
    if (csrfToken) {
      return csrfToken
    }
    pendingCsrfTokenRequest = pendingCsrfTokenRequest ?? requestCsrfToken()

    return await pendingCsrfTokenRequest
  }

  const resetCsrfToken = (): void => {
    csrfToken = null
  }

  frappeAxiosInstance.interceptors.request.use(async (requestConfig) => {
    if (
      isUnsafeRequest(requestConfig) &&
      !isEndpointRequest(requestConfig, sessionCreatingEndpoints)
    ) {
      const currentCsrfToken = await getCsrfToken()
      if (currentCsrfToken) {
        requestConfig.headers.set(csrfTokenHeaderName, currentCsrfToken)
      }
    }
    return requestConfig
  })

  frappeAxiosInstance.interceptors.response.use(
    (successResponse) => {
      if (isEndpointRequest(successResponse.config, sessionChangingEndpoints)) {
        resetCsrfToken()
      }
      return successResponse
    },
    async (requestError: unknown) => {
      // a new session or a renewed token invalidates the cached one: renew and retry once
      if (isCsrfTokenRejectedError(requestError) && requestError.config.isCsrfTokenRetry !== true) {
        resetCsrfToken()
        const retryRequestConfig: CsrfTokenRequestConfig = {
          ...requestError.config,
          isCsrfTokenRetry: true,
        }
        return await frappeAxiosInstance.request(retryRequestConfig)
      }
      throw requestError
    },
  )
}

export { installFrappeCsrfTokenHandling }
